import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { BasicInput } from '@dev/ee-components';
import { PreviewPageType } from "@dev/ee-api/ee-api-models";

import {
    TemplateFormPayloadUpdate,
    TemplateFormsCheckInvalid,
    TemplateIncrementAutosaveCounter,
} from '@/store/landingPageCreator/actions';
import { debounce } from '@/services/tools/debounce';
import { checkIsUrlInvalid } from "@/services/tools/urlValidation/checkIsUrlInvalid";
import { checkKeyCombinations } from "@/services/tools/urlValidation/checkKeyCombinations";
import { Protocol } from "@/services/tools/urlValidation/types";
import { addHttpsToTargetValue } from "@/services/tools/urlValidation/addHttpsToTargetValue";
import { ExternalWebsiteInputProps } from "@/components/FormCreator/steps/Step3EditContent/components/ExternalWebsiteInput/types";
import { ExternalWebsiteInputWrapper } from "@/components/FormCreator/steps/Step3EditContent/components/styles";

export const ExternalWebsiteInput = (props: ExternalWebsiteInputProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { pageTemplateType, setExternalWebsite, setError, value, id, isError, clientId } = props;

    const [isInteracted, setIsInteracted] = useState(false);

    const modifyFormExternalUrl = (externalUrl: string): void => {
        const formPayloadMap = {
            [PreviewPageType.ActivationViaEmail]: { DoubleOptInPage: externalUrl },
            [PreviewPageType.FormSubmit]: { FormSubmitPage: externalUrl },
            [PreviewPageType.AlreadySubscribed]: { FormAlreadySubscribedPage: externalUrl },
        };

        const formPayload = formPayloadMap[pageTemplateType];
        setExternalWebsite(externalUrl);
        if(!!formPayload) {
            dispatch(TemplateFormPayloadUpdate(clientId, {...formPayload}));
            dispatch(TemplateIncrementAutosaveCounter());
        }
        dispatch(TemplateFormsCheckInvalid());
    }

    const changeExternalWebsiteInput = (ev): void => {
        const externalUrl = ev.target.value;
        modifyFormExternalUrl(externalUrl);
        
        debounce((): void => {
            setError(checkIsUrlInvalid(externalUrl));
        });
    };

    const handleFirstKeyDownWebsite = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (isInteracted) return;
        const { isModifierPressed, isKeyCombination } = checkKeyCombinations(e);
        if (isKeyCombination) {
            setIsInteracted(true);
            return;
        }
        const target = e.target as HTMLInputElement;
        
        const autoUrl = addHttpsToTargetValue(target.value);
        if (!isModifierPressed && (!checkIsUrlInvalid(autoUrl) || !target.value.length) && e.key !== "Backspace") {
            modifyFormExternalUrl(autoUrl);
            setIsInteracted(true);
        }
    };

    const onWebsiteUrlInputBlur = (e: React.MouseEvent<Document>) => {
        const target = e.target as HTMLInputElement;
        const autoUrl = addHttpsToTargetValue(target.value);
        if (!checkIsUrlInvalid(autoUrl)) {
            modifyFormExternalUrl(autoUrl);
        }
        setError(checkIsUrlInvalid(target.value));
        setIsInteracted(false);
    };

    return (
        <ExternalWebsiteInputWrapper>
            <BasicInput
                id={`formcreator-step3-external-website-input-${id}`}
                label={t('url')}
                onChange={changeExternalWebsiteInput}
                value={value || ''}
                placeholder={Protocol.HTTPS}
                disableSentenceCase
                onKeyDown={handleFirstKeyDownWebsite}
                onBlur={onWebsiteUrlInputBlur}
                error={isError}
                errorMessage={t('pleaseentervalidurl')}
            />
        </ExternalWebsiteInputWrapper>
    )
}